/**
 * A question as it is answered (the page's and the button's), from Ask's events (askApi.ts
 * streamAsk), and a finished one from its thread. Pure: the provider (AskProvider.tsx) keeps the
 * state, this only says what each event does to it.
 */
import type { AskAnswer, AskEvent, AskNumberCheck, AskStage, AskStatusCode, AskStep, AskThreadItem } from './askApi'

export type AskRunStage = 'start' | AskStage | 'done'

export interface AskRun {
    /** Unique per question asked here, so a view can tell a new question from the one it showed. */
    key: number
    question: string
    page: string | null
    state: Record<string, string>
    threadId: string | null
    stage: AskRunStage
    steps: AskStep[]
    answer: AskAnswer | null
    numberCheck: AskNumberCheck | null
    status: 'running' | AskStatusCode
    /** Why it ended without an answer: a refusal's code or the stream's error. */
    error: string | null
    questionId: number | null
}

let nextKey = 1

export function startRun(question: string, page: string | null, state: Record<string, string>, threadId: string | null): AskRun {
    return { key: nextKey++, question, page, state, threadId, stage: 'start', steps: [], answer: null, numberCheck: null, status: 'running', error: null, questionId: null }
}

/** A step replaces the one with its id (its end after its start), or joins the list. */
function withStep(steps: AskStep[], step: AskStep): AskStep[] {
    const i = steps.findIndex((s) => s.id === step.id)
    if (i === -1) return [...steps, step]
    const next = steps.slice()
    next[i] = { ...steps[i], ...step }
    return next
}

export function reduceRun(run: AskRun, event: AskEvent): AskRun {
    switch (event.type) {
        case 'start':
            return { ...run, threadId: event.thread_id }
        case 'stage':
            return run.stage === 'done' ? run : { ...run, stage: event.stage }
        case 'step':
            return { ...run, steps: withStep(run.steps, event.step) }
        case 'answer':
            return {
                ...run,
                stage: 'done',
                status: event.status,
                answer: event.answer,
                numberCheck: event.number_check,
                // The finished steps carry their SQL (the "show the queries" list).
                steps: event.steps.length ? event.steps : run.steps,
                threadId: event.thread_id || run.threadId,
                questionId: event.question_id,
            }
        case 'error':
            return { ...run, stage: 'done', status: event.code === 'over_budget' ? 'over_budget' : 'failed', error: event.code }
        case 'done':
            return run.status === 'running' ? { ...run, stage: 'done', status: 'failed', error: 'no_answer' } : run
        default:
            return run
    }
}

/** The stream broke or was refused before it ended. */
export function failRun(run: AskRun, code: string): AskRun {
    return run.status === 'running' ? { ...run, stage: 'done', status: 'failed', error: code } : run
}

/** A finished question of a thread, shown as a run that is done. */
export function runOfItem(item: AskThreadItem): AskRun {
    return {
        key: -item.question_id,
        question: item.question,
        page: item.page,
        state: {},
        threadId: null,
        stage: 'done',
        steps: item.steps,
        answer: item.answer,
        numberCheck: item.number_check,
        status: item.status,
        error: null,
        questionId: item.question_id,
    }
}

/** What has been read so far: queries done, their rows, the views they read. */
export function countsOf(steps: AskStep[]): { queries: number; rows: number; views: number } {
    const done = steps.filter((s) => s.tool === 'query' && s.state === 'done')
    return {
        queries: done.length,
        rows: done.reduce((n, s) => n + (s.rows ?? 0), 0),
        views: new Set(done.flatMap((s) => s.views ?? [])).size,
    }
}

/** The step being read now, if any. */
export function activeStep(steps: AskStep[]): AskStep | null {
    for (let i = steps.length - 1; i >= 0; i -= 1) if (steps[i]!.state === 'running') return steps[i]!
    return null
}
